import React, { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Trophy, Target, Zap, Heart, Calendar, TrendingUp, Award, Star, Crown, Flame } from 'lucide-react'
import { Achievement, Challenge, MoodEntry } from '../types'

interface AchievementsProps {
  achievements: Achievement[]
  challenges: Challenge[]
  entries: MoodEntry[]
  onNewAchievementsSeen: () => void
}

export default function Achievements({ achievements, challenges, entries, onNewAchievementsSeen }: AchievementsProps) {
  useEffect(() => {
    // Mark new achievements as seen after viewing
    const timer = setTimeout(() => {
      onNewAchievementsSeen()
    }, 3000)
    return () => clearTimeout(timer)
  }, [onNewAchievementsSeen])

  const getAchievementIcon = (type: string) => {
    switch (type) {
      case 'streak': return <Flame className="w-6 h-6" />
      case 'entries': return <Calendar className="w-6 h-6" />
      case 'mood': return <Heart className="w-6 h-6" />
      case 'consistency': return <TrendingUp className="w-6 h-6" />
      case 'special': return <Crown className="w-6 h-6" />
      default: return <Trophy className="w-6 h-6" />
    }
  }

  const getAchievementColor = (type: string) => {
    switch (type) {
      case 'streak': return 'from-orange-400 to-red-500'
      case 'entries': return 'from-blue-400 to-indigo-500'
      case 'mood': return 'from-pink-400 to-purple-500'
      case 'consistency': return 'from-purple-400 to-emerald-500'
      case 'special': return 'from-yellow-400 to-orange-500'
      default: return 'from-gray-400 to-gray-500'
    }
  }

  const unlockedAchievements = achievements.filter(a => a.unlocked)
  const lockedAchievements = achievements.filter(a => !a.unlocked)
  const activeChallenges = challenges.filter(c => c.active && !c.completed)
  const completedChallenges = challenges.filter(c => c.completed)

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <motion.div 
        className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-6 shadow-lg border border-yellow-200"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
      >
        <div className="flex items-center space-x-3 mb-4">
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Crown className="w-8 h-8 text-yellow-600" />
          </motion.div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
            I Tuoi Successi
          </h2>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-600">{unlockedAchievements.length}</div>
            <div className="text-sm text-yellow-500">Trofei Sbloccati</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600">{completedChallenges.length}</div>
            <div className="text-sm text-orange-500">Completate</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-red-600">{activeChallenges.length}</div>
            <div className="text-sm text-red-500">Attive</div>
          </div>
        </div>
      </motion.div>

      {/* Active Challenges */}
      {activeChallenges.length > 0 && (
        <motion.div 
          className="space-y-4"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center space-x-3">
            <Target className="w-6 h-6 text-purple-600" />
            <h3 className="text-xl font-bold text-gray-800">Sfide Attive</h3>
          </div>

          {activeChallenges.map((challenge, index) => (
            <motion.div
              key={challenge.id}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 shadow-lg border border-gray-200"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3 + index * 0.1 }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Target className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800">{challenge.title}</h4>
                    <p className="text-sm text-gray-600">{challenge.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-purple-600">
                    {challenge.progress}/{challenge.target}
                  </div>
                  <div className="text-xs text-gray-500">
                    {Math.round((challenge.progress / challenge.target) * 100)}%
                  </div>
                </div>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                <motion.div
                  className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${(challenge.progress / challenge.target) * 100}%` }}
                  transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                />
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Ricompensa: {challenge.reward}</span>
                <span className="text-purple-600 font-medium">
                  {challenge.target - challenge.progress} rimanenti
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Unlocked Achievements */}
      <motion.div 
        className="space-y-4"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <div className="flex items-center space-x-3">
          <Trophy className="w-6 h-6 text-yellow-600" />
          <h3 className="text-xl font-bold text-gray-800">Trofei Sbloccati</h3>
        </div>

        <div className="grid gap-4">
          <AnimatePresence>
            {unlockedAchievements.map((achievement, index) => (
              <motion.div
                key={achievement.id}
                className={`bg-gradient-to-r ${getAchievementColor(achievement.type)} rounded-2xl p-5 shadow-lg text-white relative overflow-hidden`}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.5 + index * 0.1, type: "spring" }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-center space-x-4">
                  <motion.div
                    className="p-3 bg-white/20 rounded-full"
                    animate={{ 
                      scale: achievement.isNew ? [1, 1.2, 1] : 1,
                      rotate: achievement.isNew ? [0, 10, -10, 0] : 0
                    }}
                    transition={{ duration: 1, repeat: achievement.isNew ? Infinity : 0 }}
                  >
                    {getAchievementIcon(achievement.type)}
                  </motion.div>
                  <div className="flex-1">
                    <h4 className="font-bold text-lg">{achievement.title}</h4>
                    <p className="text-white/80">{achievement.description}</p>
                    <div className="flex items-center space-x-2 mt-2">
                      <Star className="w-4 h-4" />
                      <span className="text-sm">Sbloccato il {new Date(achievement.unlockedAt!).toLocaleDateString('it-IT')}</span>
                    </div>
                  </div>
                </div>

                {achievement.isNew && (
                  <motion.div
                    className="absolute top-2 right-2 bg-yellow-400 text-yellow-800 px-2 py-1 rounded-full text-xs font-bold"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    NUOVO!
                  </motion.div>
                )}

                {/* Sparkle effect for new achievements */}
                {achievement.isNew && (
                  <div className="absolute inset-0 pointer-events-none">
                    {[...Array(6)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-2 h-2 bg-white rounded-full"
                        style={{
                          top: `${20 + Math.random() * 60}%`,
                          left: `${20 + Math.random() * 60}%`,
                        }}
                        animate={{
                          scale: [0, 1, 0],
                          opacity: [0, 1, 0],
                        }}
                        transition={{
                          duration: 2,
                          delay: Math.random() * 2,
                          repeat: Infinity,
                        }}
                      />
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Locked Achievements */}
      {lockedAchievements.length > 0 && (
        <motion.div 
          className="space-y-4"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <div className="flex items-center space-x-3">
            <Award className="w-6 h-6 text-gray-500" />
            <h3 className="text-xl font-bold text-gray-800">Da Sbloccare</h3>
          </div>

          <div className="grid gap-3">
            {lockedAchievements.slice(0, 6).map((achievement, index) => (
              <motion.div
                key={achievement.id}
                className="bg-gray-100 rounded-2xl p-4 shadow-lg border-2 border-dashed border-gray-300 relative"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 + index * 0.1 }}
              >
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-gray-200 rounded-full">
                    {getAchievementIcon(achievement.type)}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-600">{achievement.title}</h4>
                    <p className="text-gray-500 text-sm">{achievement.description}</p>
                  </div>
                  <div className="text-2xl">🔒</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Completed Challenges */}
      {completedChallenges.length > 0 && (
        <motion.div 
          className="space-y-4"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <div className="flex items-center space-x-3">
            <Zap className="w-6 h-6 text-purple-600" />
            <h3 className="text-xl font-bold text-gray-800">Sfide Completate</h3>
          </div>

          <div className="grid gap-3">
            {completedChallenges.map((challenge, index) => (
              <motion.div
                key={challenge.id}
                className="bg-gradient-to-r from-purple-50 to-emerald-50 rounded-2xl p-4 shadow-lg border border-purple-200"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.9 + index * 0.1 }}
              >
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Zap className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-purple-800">{challenge.title}</h4>
                    <p className="text-purple-600 text-sm">Completata! {challenge.reward}</p>
                  </div>
                  <div className="text-2xl">✅</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  )
}